import { FileWithPath } from 'react-dropzone';

import {
  DROPZONE_ARCHIVE_FORMATS,
  XRAY_DICOM_FILE_EXTENSIONS,
} from '@/shared/config';
import { isDicomFile } from '@/shared/lib';

const ARCHIVE_EXTENSIONS = Object.values(DROPZONE_ARCHIVE_FORMATS).flat();

// NOTE: The archive filter uses the X-ray DICOM set (.dcm, .dic) for every
// study type. The upload form accepts .dic only for IOXRay and Pano; for CBCT
// it accepts .dcm only (ACCEPTED_FILES_FORMATS_FOR_STUDIES).
const hasDicomSuffix = (file: FileWithPath) =>
  XRAY_DICOM_FILE_EXTENSIONS.some((extension) =>
    file.name.toLowerCase().endsWith(extension),
  );

const isExtensionlessCandidate = (file: FileWithPath) =>
  !file.name.includes('.');

const isArchiveFile = (file: FileWithPath) =>
  ARCHIVE_EXTENSIONS.some((extension) =>
    file.name.toLowerCase().endsWith(extension),
  );

const dropArchives = (files: FileWithPath[]) =>
  files.filter((file) => !isArchiveFile(file));

// NOTE: Sequential reduce with short-circuit so we stop at the first DICOM
// match instead of running 3000 parallel FileReaders.
const someIsDicomByMagicBytes = (files: FileWithPath[]) =>
  files.reduce<Promise<boolean>>(
    async (accPromise, file) => (await accPromise) || isDicomFile(file),
    Promise.resolve(false),
  );

export const filterArchivesIfDicomPresent = async (
  files: FileWithPath[],
): Promise<FileWithPath[]> => {
  if (!files.some(isArchiveFile)) {
    return files;
  }

  if (files.some(hasDicomSuffix)) {
    return dropArchives(files);
  }

  const candidates = files.filter(isExtensionlessCandidate);

  if (candidates.length === 0) {
    return files;
  }

  const hasDicomMagic = await someIsDicomByMagicBytes(candidates);

  return hasDicomMagic ? dropArchives(files) : files;
};
